import { Link, useParams } from 'react-router-dom';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import { nomoStories, storyCategories } from '@/mocks/stories';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const chipTone: Record<string, string> = {
  FOOD: 'bg-primary-100 text-primary-700',
  PLACE: 'bg-secondary-100 text-secondary-700',
  PEOPLE: 'bg-accent-100 text-accent-700',
  CHARACTER: 'bg-primary-100 text-primary-700',
  PLAY: 'bg-secondary-100 text-secondary-700',
};

export default function StoryPage() {
  const { id } = useParams<{ id: string }>();
  useScrollReveal();

  const index = nomoStories.findIndex((s) => s.id === id);
  const story = index >= 0 ? nomoStories[index] : undefined;
  const prev = index > 0 ? nomoStories[index - 1] : undefined;
  const next = index >= 0 && index < nomoStories.length - 1 ? nomoStories[index + 1] : undefined;

  if (!story) {
    return (
      <div className="min-h-screen w-full bg-background-50">
        <SiteNavbar />
        <main className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="text-center">
            <p className="font-heading text-2xl text-foreground-950">这个故事还没写好</p>
            <p className="mt-3 text-sm text-foreground-600">它正在某个地方，慢慢发生着。</p>
            <Link
              to="/stories"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors duration-300 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              返回故事库
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const categoryMeta = storyCategories.find((c) => c.key === story.category);

  return (
    <div className="min-h-screen w-full bg-background-50">
      <SiteNavbar />
      <main>
        <section className="bg-background-100 pb-14 pt-28 md:pb-20 md:pt-40">
          <div className="mx-auto max-w-3xl px-4 md:px-6">
            <nav className="reveal mb-8 flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link
                to="/"
                className="transition-colors duration-300 hover:text-primary-600 cursor-pointer"
              >
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <Link
                to="/stories"
                className="transition-colors duration-300 hover:text-primary-600 cursor-pointer"
              >
                品牌故事
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <span className="text-foreground-800">{story.categoryZh}</span>
            </nav>

            <div className="reveal">
              <span
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] tracking-[0.14em] ${chipTone[story.category]}`}
              >
                {story.category}
              </span>
              <h1 className="mt-5 font-heading text-3xl leading-tight tracking-[0.04em] text-foreground-950 md:text-5xl">
                {story.title}
              </h1>
              <p className="mt-5 text-xs tracking-[0.2em] text-foreground-400 uppercase">
                {categoryMeta?.zh ?? story.categoryZh} · {story.date}
              </p>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="mx-auto max-w-3xl px-4 md:px-6">
            <div className="reveal overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
              <img
                src={story.image}
                alt={`NOMOISM 品牌故事：${story.title}`}
                title={`NOMOISM ${story.categoryZh} · ${story.title}`}
                className="h-[280px] w-full object-cover object-top md:h-[400px]"
              />
            </div>

            <div className="mt-12 space-y-6">
              {story.body.map((paragraph, idx) => (
                <p
                  key={paragraph}
                  className="reveal text-sm leading-loose text-foreground-700 md:text-base md:leading-loose"
                  style={{ transitionDelay: `${idx * 80}ms` }}
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="reveal mt-16 border-t border-background-200/70 pt-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  {prev ? (
                    <Link
                      to={`/stories/${prev.id}`}
                      className="group inline-flex items-center gap-2 text-sm text-foreground-600 transition-colors duration-300 hover:text-primary-600 cursor-pointer"
                    >
                      <i className="ri-arrow-left-line transition-transform duration-300 group-hover:-translate-x-1"></i>
                      <span className="max-w-[220px] truncate">{prev.title}</span>
                    </Link>
                  ) : (
                    <span className="text-sm text-foreground-300">已是第一篇</span>
                  )}
                </div>
                <div className="text-right">
                  {next ? (
                    <Link
                      to={`/stories/${next.id}`}
                      className="group inline-flex items-center gap-2 text-sm text-foreground-600 transition-colors duration-300 hover:text-primary-600 cursor-pointer"
                    >
                      <span className="max-w-[220px] truncate">{next.title}</span>
                      <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                    </Link>
                  ) : (
                    <span className="text-sm text-foreground-300">已是最后一篇</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}